import React, { useEffect, useState, useContext, useCallback } from 'react';
import { request } from '../api';
import { deleteEvent as deleteSupabaseEvent } from '../utils/eventsApi';
import { listReports, deleteReport } from '../utils/adminApi';
import ToastContext from '../contexts/ToastContext';
import { FiFlag, FiTrash2, FiX, FiAlertCircle } from 'react-icons/fi';
import { isDemoAdmin, DEMO_ADMIN_MESSAGE } from '../utils/adminDemoMode';
import { useAuth } from '../contexts/AuthContext';

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const toast = useContext(ToastContext);
  const { currentUser } = useAuth();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listReports();
      setReports(data.filter(r => r.event));
    } catch (err) {
      console.error(err);
      if (toast && toast.error) toast.error('Erreur lors du chargement des signalements');
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  async function dismissReport(id) {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      return;
    }
    try {
      await deleteReport(id);
      setReports(reports.filter(r => r._id !== id));
      if (toast && toast.success) toast.success('Signalement rejeté');
      setConfirmAction(null);
    } catch (err) {
      if (toast && toast.error) toast.error('Erreur lors du rejet');
    }
  }

  async function deleteEvent(eventId) {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      return;
    }
    try {
      await deleteSupabaseEvent(eventId);
      await fetchReports();
      if (toast && toast.success) toast.success('Événement supprimé');
      setConfirmAction(null);
    } catch (err) {
      if (toast && toast.error) toast.error('Erreur lors de la suppression');
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--muted)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', animation: 'spin 1s linear infinite' }}>⏳</div>
          Chargement des signalements...
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'transparent' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 1.5rem', animation: 'slideUpFade 0.6s ease-out both' }}>
        
        {/* Header */}
        <div style={{ marginBottom: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '2.5rem' }}>🚩</div>
            <div>
              <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', fontWeight: 900, margin: 0, background: 'linear-gradient(135deg, var(--accent), var(--accent-2))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.5px' }}>
                Signalements d'événements
              </h1>
              <p style={{ fontSize: '1.05rem', color: 'var(--muted)', margin: '0.5rem 0 0 0', fontWeight: 500 }}>
                {reports.length} signalement{reports.length !== 1 ? 's' : ''} à examiner
              </p>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {reports.length === 0 ? (
          <div style={{
            padding: '4rem 2rem',
            textAlign: 'center',
            borderRadius: '20px',
            border: '1.5px dashed rgba(56, 189, 248, 0.2)',
            background: 'rgba(56, 189, 248, 0.03)',
            animation: 'slideUpFade 0.6s ease-out 0.1s both'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.6 }}>✓</div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
              Aucun signalement
            </h3>
            <p style={{ color: 'var(--muted)', fontSize: '1rem' }}>
              Tous les événements sont en conformité. Bon travail ! 🎉
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1.5rem', animation: 'slideUpFade 0.6s ease-out 0.1s both' }}>
            {reports.map((report, idx) => (
              <div
                key={report._id}
                style={{
                  background: 'var(--surface)',
                  borderRadius: '16px',
                  border: '1.5px solid rgba(56, 189, 248, 0.12)',
                  padding: '1.75rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  animation: `slideUpFade 0.6s ease-out ${0.2 + idx * 0.05}s both`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(56, 189, 248, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.12)';
                }}
              >
                {/* Event Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1.25rem', borderBottom: '1px solid rgba(56, 189, 248, 0.1)' }}>
                  <div style={{ fontSize: '1.8rem', background: 'rgba(56, 189, 248, 0.1)', padding: '0.75rem', borderRadius: '12px' }}>
                    ⚽
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--text)', margin: '0 0 0.25rem 0' }}>
                      {report.event.sportType}
                    </h3>
                    <p style={{ fontSize: '0.95rem', color: 'var(--muted)', margin: 0, fontWeight: 500 }}>
                      📍 {report.event.location}
                    </p>
                  </div>
                  <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', padding: '0.5rem 1rem', borderRadius: '10px', fontWeight: 700, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FiAlertCircle size={16} /> Signalé
                  </div>
                </div>

                {/* Report Details Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.75rem' }}>
                  
                  {/* Organizer */}
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '0.5rem' }}>
                      👤 Organisateur
                    </label>
                    <p style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text)', margin: 0 }}>
                      {report.event.organizer?.displayName || 'Inconnu'}
                    </p>
                  </div>

                  {/* Reported By */}
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '0.5rem' }}>
                      🚩 Signalé par
                    </label>
                    <p style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text)', margin: 0 }}>
                      {report.reportedBy.displayName}
                    </p>
                    <p style={{ fontSize: '0.85rem', color: 'var(--muted)', margin: '0.25rem 0 0 0' }}>
                      {report.reportedBy.email}
                    </p>
                  </div>

                  {/* Date */}
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '0.5rem' }}>
                      📅 Date du signalement
                    </label>
                    <p style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text)', margin: 0 }}>
                      {new Date(report.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                    <p style={{ fontSize: '0.85rem', color: 'var(--muted)', margin: '0.25rem 0 0 0' }}>
                      {new Date(report.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>

                {/* Reason Section */}
                <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.05)', borderLeft: '3px solid #ef4444', borderRadius: '8px', marginBottom: '1.75rem' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '0.5rem' }}>
                    📝 Raison du signalement
                  </label>
                  <p style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text)', margin: 0, lineHeight: 1.5 }}>
                    {report.reason}
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setConfirmAction({ type: 'delete', reportId: report._id, eventId: report.event._id })}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '180px',
                      padding: '0.85rem 1.5rem',
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '11px',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
                    }}
                  >
                    <FiTrash2 size={18} /> Supprimer l'événement
                  </button>
                  <button
                    onClick={() => setConfirmAction({ type: 'reject', reportId: report._id })}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '180px',
                      padding: '0.85rem 1.5rem',
                      background: 'rgba(107, 114, 128, 0.1)',
                      color: '#6b7280',
                      border: '1.5px solid rgba(107, 114, 128, 0.3)',
                      borderRadius: '11px',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(107, 114, 128, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(107, 114, 128, 0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <FiX size={18} /> Rejeter le signalement
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmAction && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              animation: 'fadeIn 0.3s ease-out'
            }}
            onClick={() => setConfirmAction(null)}
          >
            <div
              style={{
                background: 'var(--surface)',
                borderRadius: '20px',
                padding: '2rem',
                maxWidth: '500px',
                width: '90%',
                border: '1.5px solid rgba(56, 189, 248, 0.2)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'slideUpFade 0.3s ease-out'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', marginBottom: '1rem', marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {confirmAction.type === 'delete' ? '⚠️ Confirmer la suppression' : '❓ Rejeter le signalement'}
              </h2>
              <p style={{ color: 'var(--text)', marginBottom: '2rem', lineHeight: 1.6, fontSize: '1rem' }}>
                {confirmAction.type === 'delete'
                  ? 'Êtes-vous sûr de vouloir supprimer cet événement ? Cette action est irréversible.'
                  : 'Rejeter ce signalement signifie qu\'aucune action ne sera prise sur l\'événement.'}
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setConfirmAction(null)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(56, 189, 248, 0.1)',
                    color: 'var(--accent)',
                    border: '1.5px solid rgba(56, 189, 248, 0.3)',
                    borderRadius: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(56, 189, 248, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(56, 189, 248, 0.1)';
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    if (confirmAction.type === 'delete') {
                      deleteEvent(confirmAction.eventId);
                    } else {
                      dismissReport(confirmAction.reportId);
                    }
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: confirmAction.type === 'delete' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'linear-gradient(135deg, var(--accent), var(--accent-2))',
                    color: 'white',
                    border: 'none',
                    borderRadius: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {confirmAction.type === 'delete' ? 'Supprimer l\'événement' : 'Rejeter'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}